import React from "react";

/**
 * Animated blueprint background: dotted grid, floating geometry,
 * rulers, and compass tick marks. Pure CSS + SVG, GPU friendly.
 */
export const BlueprintBackground = () => {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {/* Deep blueprint base */}
            <div className="absolute inset-0 bg-[#07152E]" />

            {/* Subtle radial vignette */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 30% 20%, rgba(0,122,255,0.18), transparent 60%), radial-gradient(ellipse at 80% 85%, rgba(0,163,255,0.12), transparent 55%)",
                }}
            />

            {/* Fine grid layer, drifting */}
            <div
                className="absolute inset-0 animate-grid-drift"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(80,140,220,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(80,140,220,0.18) 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                }}
            />
            {/* Major grid, moving opposite */}
            <div
                className="absolute inset-0 animate-grid-drift-slow"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(120,170,240,0.22) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,170,240,0.22) 1px, transparent 1px)",
                    backgroundSize: "140px 140px",
                }}
            />

            {/* Floating blueprint SVGs */}
            <svg
                className="absolute top-[6%] left-[4%] w-[320px] h-[320px] text-[#4EA3FF] opacity-25 animate-float-slow"
                viewBox="0 0 200 200"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.7"
            >
                {/* Floor-plan-like rectangles */}
                <rect x="20" y="20" width="160" height="100" />
                <rect x="20" y="20" width="70" height="40" />
                <rect x="90" y="20" width="90" height="50" />
                <rect x="90" y="70" width="90" height="50" />
                <line x1="20" y1="60" x2="90" y2="60" />
                <line x1="55" y1="60" x2="55" y2="120" />
                {/* Dim/arrow */}
                <line x1="20" y1="140" x2="180" y2="140" />
                <line x1="20" y1="135" x2="20" y2="145" />
                <line x1="180" y1="135" x2="180" y2="145" />
                <text x="95" y="136" fontSize="6" fill="currentColor" strokeWidth="0">
                    42'-6"
                </text>
            </svg>

            <svg
                className="absolute top-[18%] right-[8%] w-[260px] h-[260px] text-[#00A3FF] opacity-20 animate-float-fast"
                viewBox="0 0 100 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.4"
            >
                {/* Compass circle with tick marks */}
                <circle cx="50" cy="50" r="40" />
                <circle cx="50" cy="50" r="30" />
                {Array.from({ length: 24 }).map((_, i) => {
                    const a = (i * Math.PI) / 12;
                    const x1 = 50 + Math.cos(a) * 40;
                    const y1 = 50 + Math.sin(a) * 40;
                    const x2 = 50 + Math.cos(a) * (i % 6 === 0 ? 32 : 36);
                    const y2 = 50 + Math.sin(a) * (i % 6 === 0 ? 32 : 36);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
                })}
                <line x1="50" y1="10" x2="50" y2="90" />
                <line x1="10" y1="50" x2="90" y2="50" />
                <text x="46" y="16" fontSize="5" fill="currentColor" strokeWidth="0">
                    N
                </text>
            </svg>

            <svg
                className="absolute bottom-[10%] left-[14%] w-[360px] h-[120px] text-[#66B5FF] opacity-25 animate-float-mid"
                viewBox="0 0 400 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
            >
                {/* Ruler */}
                <rect x="10" y="30" width="380" height="40" />
                {Array.from({ length: 39 }).map((_, i) => (
                    <line
                        key={i}
                        x1={10 + i * 10}
                        y1="30"
                        x2={10 + i * 10}
                        y2={i % 5 === 0 ? 45 : 38}
                    />
                ))}
                <text x="15" y="60" fontSize="10" fill="currentColor" strokeWidth="0">
                    0
                </text>
                <text x="185" y="60" fontSize="10" fill="currentColor" strokeWidth="0">
                    20
                </text>
                <text x="350" y="60" fontSize="10" fill="currentColor" strokeWidth="0">
                    40
                </text>
            </svg>

            <svg
                className="absolute bottom-[20%] right-[6%] w-[280px] h-[280px] text-[#7FC6FF] opacity-20 animate-float-slow"
                viewBox="0 0 100 100"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
            >
                {/* Isometric cube wireframe */}
                <polygon points="50,12 86,32 86,72 50,92 14,72 14,32" />
                <line x1="50" y1="12" x2="50" y2="52" />
                <line x1="14" y1="32" x2="50" y2="52" />
                <line x1="86" y1="32" x2="50" y2="52" />
                <line x1="50" y1="52" x2="50" y2="92" />
            </svg>

            {/* Drifting blueprint lines */}
            <svg
                className="absolute inset-0 w-full h-full opacity-30"
                viewBox="0 0 1920 1080"
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id="beam" x1="0" x2="1" y1="0" y2="0">
                        <stop offset="0" stopColor="#00A3FF" stopOpacity="0" />
                        <stop offset="0.5" stopColor="#00A3FF" stopOpacity="0.9" />
                        <stop offset="1" stopColor="#00A3FF" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <line
                    x1="0"
                    y1="260"
                    x2="1920"
                    y2="260"
                    stroke="url(#beam)"
                    strokeWidth="1.5"
                    className="animate-beam-1"
                />
                <line
                    x1="0"
                    y1="680"
                    x2="1920"
                    y2="680"
                    stroke="url(#beam)"
                    strokeWidth="1.5"
                    className="animate-beam-2"
                />
                <line
                    x1="620"
                    y1="0"
                    x2="620"
                    y2="1080"
                    stroke="url(#beam)"
                    strokeWidth="1.5"
                    className="animate-beam-3"
                />
            </svg>
        </div>
    );
};

export default BlueprintBackground;
